import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Users, Camera as CameraIcon, TrendingUp, Calendar, FileText, Receipt, CalendarPlus, X, ArrowRight } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { Navigation } from '../components/Navigation';
import { GlobalSearch } from '../components/GlobalSearch';
import { InstallPrompt, NotificationOptIn } from '../components/PWAPrompts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ONBOARDING_DISMISS_KEY = 'pbk_onboarding_dismissed';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [whatsNext, setWhatsNext] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, whatsNextRes, settingsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/dashboard/whats-next`, { withCredentials: true }),
        axios.get(`${API}/settings`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setWhatsNext(whatsNextRes.data.items);
      const dismissed = localStorage.getItem(ONBOARDING_DISMISS_KEY) === '1';
      setShowOnboarding(!settingsRes.data?.booking_slug && !dismissed);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching dashboard data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const dismissOnboarding = () => {
    localStorage.setItem(ONBOARDING_DISMISS_KEY, '1');
    setShowOnboarding(false);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getItemIcon = (type) => {
    switch (type) {
      case 'meeting':
        return <Calendar className="w-4 h-4" />;
      case 'production':
        return <CameraIcon className="w-4 h-4" />;
      case 'document':
        return <FileText className="w-4 h-4" />;
      case 'invoice':
        return <Receipt className="w-4 h-4" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="max-w-2xl mx-auto p-4">
          <div className="text-text-secondary">Loading...</div>
        </div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-1" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-text-secondary">Your business at a glance</p>
        </div>

        <GlobalSearch />

        {showOnboarding && (
          <div
            className="bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/30 rounded-xl p-4 flex items-start gap-3"
            data-testid="onboarding-banner"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <CalendarPlus className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-foreground mb-1">Set up your booking page</h3>
              <p className="text-sm text-text-secondary mb-3">
                Start accepting clients directly from your website — pick a URL, grab the code, done.
              </p>
              <button
                onClick={() => navigate('/settings/booking-widget')}
                className="inline-flex items-center gap-1.5 bg-primary text-background px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                data-testid="onboarding-cta"
              >
                Get started <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={dismissOnboarding}
              className="text-text-muted hover:text-foreground p-1 rounded-lg transition-colors"
              aria-label="Dismiss"
              data-testid="onboarding-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-surface border border-border rounded-xl p-4" data-testid="stat-card-income">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-success" />
              </div>
              <span className="text-xs text-text-secondary uppercase tracking-wider">Income</span>
            </div>
            <div className="text-2xl font-bold text-success">${stats?.income?.toFixed(2) || '0.00'}</div>
          </div>

          <div className="bg-surface border border-border rounded-xl p-4" data-testid="stat-card-outstanding">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-alert/10 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-alert" />
              </div>
              <span className="text-xs text-text-secondary uppercase tracking-wider">Outstanding</span>
            </div>
            <div className="text-2xl font-bold text-alert">${stats?.outstanding?.toFixed(2) || '0.00'}</div>
          </div>

          <div className="bg-surface border border-border rounded-xl p-4" data-testid="stat-card-clients">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="w-4 h-4 text-primary" />
              </div>
              <span className="text-xs text-text-secondary uppercase tracking-wider">Clients</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{stats?.clients || 0}</div>
          </div>

          <div className="bg-surface border border-border rounded-xl p-4" data-testid="stat-card-shoots">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <CameraIcon className="w-4 h-4 text-primary" />
              </div>
              <span className="text-xs text-text-secondary uppercase tracking-wider">Shoots</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{stats?.shoots || 0}</div>
          </div>
        </div>

        <InstallPrompt />
        <NotificationOptIn />

        <div>
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4" data-testid="whats-next-title">
            What&apos;s Next
          </h2>
          {whatsNext.length === 0 ? (
            <div className="bg-surface border border-border rounded-xl p-8 text-center" data-testid="whats-next-empty">
              <p className="text-text-secondary mb-2">All caught up!</p>
              <p className="text-sm text-text-muted">No urgent items right now.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {whatsNext.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => navigate(`/clients/${item.client_id}`)}
                  className={`w-full bg-surface border rounded-xl p-4 text-left hover:border-primary/50 transition-all ${
                    index === 0 ? 'border-primary/30' : 'border-border'
                  }`}
                  data-testid={`whats-next-item-${item.type}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-surface-raised flex items-center justify-center text-primary flex-shrink-0">
                      {getItemIcon(item.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-foreground truncate">{item.title}</h3>
                        <StatusBadge status={item.status} />
                      </div>
                      <p className="text-sm text-text-secondary truncate">{item.client_name}</p>
                      {item.date && (
                        <p className="text-xs text-text-muted mt-1">
                          {item.date} {item.time && `at ${item.time}`}
                        </p>
                      )}
                      {item.amount && (
                        <p className="text-sm text-foreground mt-1 font-medium">${item.amount.toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <Navigation />
    </div>
  );
}
